import algosdk from 'algosdk';
import { AbelGhostSDK, MAINNET_TINYMAN_V2_APP_ID } from 'abel-ghost-sdk';
import { AlgorandClient } from '@algorandfoundation/algokit-utils';

// abel-ghost-sdk batches on-chain reads into fee-free `simulate` calls — one
// round-trip per 126 accounts for a rekey target, per 42 for a whole pool —
// instead of a per-account /v2/accounts fetch plus a per-asset /v2/assets fetch.
// Pool data is only used for mainnet detection, so this points at the same
// mainnet algod the app uses.
const algorand = AlgorandClient.fromConfig({
  algodConfig: { server: 'https://mainnet-api.algonode.cloud', port: 443 },
});
// The SDK's reader program, deployed on mainnet and byte-identical to the TEAL
// the SDK ships. Left unset, the SDK sends each call as an app *create* and
// re-uploads that 2180-byte program every time; naming the app roughly halves
// what we upload. If it is ever updated or deleted the calls throw, and the
// catch blocks below fall back to generic labels rather than wrong ones.
const ABEL_READER_APP_ID = 3381542955n;
const sdk = new AbelGhostSDK({ algorand, ghostAppId: ABEL_READER_APP_ID, concurrency: 6 });

// Every Tinyman v2 pool escrow is rekeyed to the Tinyman app's own account, so
// the rekey target is the cheapest "might be a pool" test there is.
const TM2_APP_ADDR = algosdk.getApplicationAddress(MAINNET_TINYMAN_V2_APP_ID).toString();

// Narrows the list the pool read below has to ask about. A rekey target costs
// one resource reference per account where a pool read costs three, so this
// covers 126 accounts per round-trip against `getTinymanInfo`'s 42, and pools
// are a small minority of the addresses on a page. It buys round-trips at the
// cost of latency, not the reverse — the pre-filter has to finish before the
// pool read can start.
//
// On failure it hands back the full list: losing an optimization should cost
// round-trips, not labels.
async function tinymanCandidates(addresses) {
  const t0 = performance.now();
  try {
    const authAddrs = await sdk.getAuthAddrs(addresses);
    const candidates = addresses.filter(addr => authAddrs.get(addr) === TM2_APP_ADDR);
    const ms = Math.round(performance.now() - t0);
    console.log(`abel getAuthAddrs: ${addresses.length} addrs -> ${candidates.length} rekeyed to Tinyman in ${ms}ms`);
    return candidates;
  } catch (e) {
    const ms = Math.round(performance.now() - t0);
    console.error(`abel getAuthAddrs failed after ${ms}ms (${addresses.length} addrs) — falling back to a pool read over all of them`, e);
    return addresses;
  }
}

// Batched Tinyman v2 pool lookup. Returns a Map of address -> pool info:
// `{ validRekey, asset1Id, asset1UnitName, asset2Id, asset2UnitName }`.
// Addresses cut by the pre-filter are absent rather than `validRekey: false`;
// callers test `validRekey`, so both read the same.
export async function lookupTinymanPools(addresses) {
  if (!addresses.length) return new Map();
  const candidates = await tinymanCandidates(addresses);
  if (!candidates.length) return new Map();
  const t0 = performance.now();
  try {
    const result = await sdk.getTinymanInfo(candidates);
    const ms = Math.round(performance.now() - t0);
    let hits = 0;
    for (const v of result.values()) if (v?.validRekey) hits++;
    console.log(`abel getTinymanInfo: ${candidates.length} addrs -> ${hits} pools in ${ms}ms`);
    return result;
  } catch (e) {
    const ms = Math.round(performance.now() - t0);
    console.error(`abel getTinymanInfo failed after ${ms}ms (${candidates.length} addrs)`, e);
    return new Map();
  }
}

// Batched PACT pool lookup, keyed by pool app ID (bigint — the same type escreg
// hands back). Returns a Map of app ID -> `{ type, asset1Id, asset1UnitName,
// asset2Id, asset2UnitName, feeBps }`.
//
// The SDK authenticates each app from algod alone, with no PACT API: global
// state shape picks the pool type, and that type's PACT creator — stamped by
// consensus at app creation, so unforgeable — has to match. Apps that fail come
// back as `type: ""`, so the "is a pool" test is `type !== ""` and not a truthy
// asset ID: ALGO is PACT's *primary* asset, so `asset1Id === 0` is normal.
// Creators default to PACT's mainnet ones, hence mainnet-only.
export async function lookupPactPools(appIds) {
  if (!appIds.length) return new Map();
  const t0 = performance.now();
  try {
    const result = await sdk.getPactInfo(appIds);
    const ms = Math.round(performance.now() - t0);
    let hits = 0;
    for (const v of result.values()) if (v?.type) hits++;
    console.log(`abel getPactInfo: ${appIds.length} apps -> ${hits} pools in ${ms}ms`);
    return result;
  } catch (e) {
    const ms = Math.round(performance.now() - t0);
    console.error(`abel getPactInfo failed after ${ms}ms (${appIds.length} apps)`, e);
    return new Map();
  }
}
